// src/components/forms/shared/SurchargeRow.tsx

import React from 'react';
import { Box, Typography, styled } from '@mui/material';
import { Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import { getCustomIcon } from '../../../utils/customIcons';
import { StyledIconButton } from '../../common/Buttons';

interface SurchargeRowProps {
  surchargeId: number;
  reason: string;
  amount: number;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  disabled?: boolean;
}

// Styled container for the entire row - matches PetSelectionRow
const RowContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  maxWidth: '800px',
  minHeight: '4rem',
  padding: '0 0',
  borderBottom: `2px dashed ${theme.palette.dividerContrast}`,
  transition: 'opacity 0.2s ease',

  '&:last-child': {
    borderBottom: 'none',
  },
}));

// Desktop Left side: Icon + Reason + Amount with ellipsis on overflow
const LeftContentDesktop = styled(Box)(({ theme }) => ({
  display: 'none',

  // Desktop: show this version with ellipses on overflow
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    flex: 1,
    minWidth: 0,
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    scrollBehavior: 'smooth',
    borderRight: `4px solid ${theme.palette.dividerContrast}`,
    boxShadow: `inset -2.5rem 0 1.5rem -1rem ${
      theme.palette.mode === 'dark'
        ? 'rgba(0, 0, 0, 0.4)'
        : 'rgba(0, 0, 0, 0.15)'
    }`,
    paddingLeft: '1rem',
  },
}));

// Mobile Left side: Icon + Reason + Amount - scrollable
const LeftContentMobile = styled(Box)(({ theme }) => ({
  display: 'none',

  // Mobile: show this version
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    flex: 1,
    minWidth: 0,
    overflowX: 'auto',
    overflowY: 'hidden',
    width: '100%',
    paddingRight: '0.5rem',
    WebkitOverflowScrolling: 'touch',
    scrollBehavior: 'smooth',
    borderRight: `4px solid ${theme.palette.dividerContrast}`,
    boxShadow: `inset -2.5rem 0 1.5rem -1rem ${
      theme.palette.mode === 'dark'
        ? 'rgba(0, 0, 0, 0.4)'
        : 'rgba(0, 0, 0, 0.15)'
    }`,

    // Hide scrollbar while keeping scroll functionality
    scrollbarWidth: 'none',

    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
}));

// Surcharge icon container - same dimensions as PetSelectionRow
const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '3rem',
  height: '3rem',
  flexShrink: 0,

  '& svg': {
    fontSize: '2.5rem',
    color: theme.palette.text.primary,
  },
}));

// Reason text - 2rem font to match PetNameText
const ReasonText = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  userSelect: 'none',
  minWidth: 0,
  lineHeight: 1.3,

  // Desktop: allow shrinking for ellipsis
  [theme.breakpoints.up('md')]: {
    flexShrink: 1,
  },

  // Mobile: prevent shrinking to keep content wide for scrolling
  [theme.breakpoints.down('md')]: {
    flexShrink: 0,
  },
}));

// Amount text - secondary color, smaller font
const AmountText = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  userSelect: 'none',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  paddingRight: '0.5rem',
}));

// Right side: Action Icons
const RightContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.2rem',
  flexShrink: 0,
  marginLeft: 'auto',
  flex: 0.2,
  justifyContent: 'flex-end',
  [theme.breakpoints.down('sm')]: {
    paddingRight: '0.5rem',
  },
}));

const SurchargeRow: React.FC<SurchargeRowProps> = ({
  surchargeId,
  reason,
  amount,
  onEdit,
  onDelete,
  disabled = false,
}) => {
  return (
    <RowContainer>
      {/* DESKTOP: with ellipses on overflow */}
      <LeftContentDesktop>
        {/* Surcharge Icon */}
        <IconContainer>{getCustomIcon('surcharge', '2.5rem')}</IconContainer>

        {/* Reason */}
        <ReasonText>{reason}</ReasonText>

        {/* Amount */}
        <AmountText> - R{amount.toFixed(2)}</AmountText>
      </LeftContentDesktop>

      {/* MOBILE: scrollable */}
      <LeftContentMobile>
        {/* Surcharge Icon */}
        <IconContainer>{getCustomIcon('surcharge', '2.5rem')}</IconContainer>

        {/* Reason */}
        <ReasonText>{reason}</ReasonText>

        {/* Amount */}
        <AmountText> - R{amount.toFixed(2)}</AmountText>
      </LeftContentMobile>

      {/* Right: Edit + Delete Icons */}
      <RightContent>
        {/* Edit Icon */}
        <StyledIconButton
          variant="bold"
          onClick={() => onEdit(surchargeId)}
          disabled={disabled}
          sx={{
            '& svg': {
              fontSize: '2rem',
            },
          }}
          title="Edit surcharge"
        >
          <EditIcon sx={{ fontSize: '1.8rem' }} />
        </StyledIconButton>

        {/* Delete Icon (rotated + icon) */}
        <StyledIconButton
          variant="delete"
          color="error"
          onClick={() => onDelete(surchargeId)}
          disabled={disabled}
          sx={{
            '& svg': {
              fontSize: '2.75rem',
            },
          }}
          title="Delete surcharge"
        >
          <AddIcon
            sx={{
              transform: 'rotate(45deg)',
            }}
          />
        </StyledIconButton>
      </RightContent>
    </RowContainer>
  );
};

export default SurchargeRow;
